import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  AIDecompositionResult, Comment, GraphEdge, GraphNode, ID, ReactionType,
  StanceSurvey, Strain, StrainGraph, User, VariantIntent,
} from "@/types";
import { apiClient, setUnauthorizedHandler } from "@/lib/api-client";

interface ISState {
  theme: "dark" | "light";
  toggleTheme: () => void;
  setTheme: (t: "dark" | "light") => void;

  graphs: StrainGraph[];
  strains: Strain[];
  comments: Comment[];
  users: User[];
  loading: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
  loadStrains: (filter?: string) => Promise<void>;
  loadFeedGraphs: () => Promise<void>;
  loadGraph: (slug: string, force?: boolean) => Promise<StrainGraph | undefined>;
  loadUsers: () => Promise<void>;

  user: User | null;
  loggedIn: boolean;
  login: (username: string, password: string) => Promise<void>;
  signup: (body: { username: string; email: string; password: string; display_name?: string }) => Promise<void>;
  loginDemo: () => Promise<void>;
  logout: () => Promise<void>;

  reactions: Record<ID, ReactionType[]>;
  toggleReaction: (targetId: ID, type: ReactionType) => Promise<void>;
  hasReaction: (targetId: ID, type: ReactionType) => boolean;

  stances: Record<ID, StanceSurvey>;
  setStance: (strainId: ID, versionId: ID, before: number, after: number, note?: string, movedByNode?: ID) => Promise<void>;
  getStance: (strainId: ID) => StanceSurvey | undefined;

  positionOverrides: Record<string, Record<ID, { x: number; y: number }>>;
  updateNodePosition: (slug: string, nodeId: ID, x: number, y: number) => Promise<void>;
  getPositionOverride: (slug: string, nodeId: ID) => { x: number; y: number } | undefined;
  clearPositionOverrides: (slug: string) => void;

  selectedNodeId: ID | null;
  selectedEdgeId: ID | null;
  selectNode: (id: ID | null) => void;
  selectEdge: (id: ID | null) => void;

  explorerMode: "canvas" | "spine" | "cruxes" | "lineage" | "diff" | "stats" | "comments";
  setExplorerMode: (m: ISState["explorerMode"]) => void;

  getGraph: (slug: string) => StrainGraph | undefined;
  addVariant: (slug: string, nodeId: ID, body: { intent: VariantIntent; text: string; note?: string }) => Promise<void>;
  preferVariant: (slug: string, nodeId: ID, variantId: ID) => Promise<void>;
  addModifier: (slug: string, targetNodeId: ID, kind: "exception" | "caveat", text: string) => Promise<void>;
  promoteModifier: (slug: string, nodeId: ID) => Promise<void>;
  addNode: (slug: string, node: Omit<GraphNode, "id">) => Promise<GraphNode | undefined>;
  updateNode: (slug: string, nodeId: ID, patch: Partial<GraphNode>) => Promise<void>;
  removeNode: (slug: string, nodeId: ID) => Promise<void>;
  addEdge: (slug: string, edge: Omit<GraphEdge, "id">) => Promise<GraphEdge | undefined>;
  removeEdge: (slug: string, edgeId: ID) => Promise<void>;
  acceptInferredNode: (slug: string, nodeId: ID) => Promise<void>;
  rejectInferredNode: (slug: string, nodeId: ID) => Promise<void>;

  drafts: Record<string, AIDecompositionResult & { originalText: string }>;
  saveDraft: (key: string, draft: AIDecompositionResult & { originalText: string }) => void;
  publishDraft: (key: string, slug: string, title: string, summary: string, tags: string[], visibility?: "public" | "unlisted" | "private") => Promise<string>;

  forkStrain: (slug: string, intent: string, scope: string, changeSummary: string) => Promise<string>;
  addComment: (c: Omit<Comment, "id" | "created_at" | "status" | "author" | "author_avatar">) => Promise<void>;
  reset: () => void;
}

function upsertGraph(graphs: StrainGraph[], graph: StrainGraph): StrainGraph[] {
  const exists = graphs.some((g) => g.strain.slug === graph.strain.slug);
  return exists ? graphs.map((g) => (g.strain.slug === graph.strain.slug ? graph : g)) : [...graphs, graph];
}

function upsertStrain(strains: Strain[], strain: Strain): Strain[] {
  const exists = strains.some((s) => s.id === strain.id);
  return exists ? strains.map((s) => (s.id === strain.id ? strain : s)) : [...strains, strain];
}

function replaceNode(graph: StrainGraph, node: GraphNode): StrainGraph {
  return { ...graph, nodes: graph.nodes.map((n) => (n.id === node.id ? node : n)) };
}

function inferTargetType(state: ISState, targetId: ID): "strain" | "node" | "edge" | "version" {
  if (state.graphs.some((g) => g.strain.id === targetId)) return "strain";
  if (state.graphs.some((g) => g.version.id === targetId)) return "version";
  if (state.graphs.some((g) => g.edges.some((e) => e.id === targetId))) return "edge";
  return "node";
}

export const useStore = create<ISState>()(
  persist(
    (set, get) => ({
      theme: "dark",
      toggleTheme: () => set((s) => ({ theme: s.theme === "dark" ? "light" : "dark" })),
      setTheme: (t) => set({ theme: t }),

      graphs: [],
      strains: [],
      comments: [],
      users: [],
      loading: false,
      error: null,

      // strain metadata + users + session only; graphs load lazily
      // (bulk for the feed via loadFeedGraphs, per-strain via loadGraph)
      hydrate: async () => {
        set({ loading: true, error: null });
        try {
          const [strains, users] = await Promise.all([
            apiClient.getStrains(),
            apiClient.getUsers(),
          ]);
          let user: User | null = null;
          try { user = await apiClient.me(); } catch {}
          set({ strains, users, user, loggedIn: !!user, loading: false });
        } catch (e: any) {
          set({ error: e?.message ?? "Failed to load app data", loading: false });
        }
      },

      loadFeedGraphs: async () => {
        const loaded = new Set(get().graphs.map((g) => g.strain.slug));
        const missing = get().strains.filter((strain) => !loaded.has(strain.slug)).map((s) => s.slug);
        if (get().strains.length > 0 && missing.length === 0) return;
        const graphs = await apiClient.getGraphs(missing.length > 0 && loaded.size > 0 ? missing : undefined);
        set((s) => {
          let next = s.graphs;
          for (const graph of graphs) next = upsertGraph(next, graph);
          return { graphs: next };
        });
      },

      loadStrains: async (filter = "new") => {
        const strains = await apiClient.getStrains(filter);
        set({ strains });
        await get().loadFeedGraphs();
      },

      loadGraph: async (slug, force = false) => {
        const existing = get().graphs.find((g) => g.strain.slug === slug);
        if (existing && !force) return existing;
        const graph = await apiClient.getGraphBySlug(slug);
        let comments: Comment[] = [];
        try { comments = await apiClient.getComments(graph.strain.id); } catch {}
        set((s) => ({
          graphs: upsertGraph(s.graphs, graph),
          strains: upsertStrain(s.strains, graph.strain),
          comments: [
            ...s.comments.filter((c) => c.strain_id !== graph.strain.id),
            ...comments,
          ],
        }));
        return graph;
      },

      loadUsers: async () => {
        const users = await apiClient.getUsers();
        set({ users });
      },

      user: null,
      loggedIn: false,
      login: async (username, password) => {
        const user = await apiClient.login({ username, password });
        set({ user, loggedIn: true });
      },
      signup: async (body) => {
        const user = await apiClient.signup(body);
        set({ user, loggedIn: true });
      },
      loginDemo: async () => {
        const user = await apiClient.login({ username: "you", password: "password123" });
        set({ user, loggedIn: true });
      },
      logout: async () => {
        try { await apiClient.logout(); } catch {}
        set({ user: null, loggedIn: false });
      },

      reactions: {},
      toggleReaction: async (targetId, type) => {
        const state = get();
        const cur = state.reactions[targetId] ?? [];
        const has = cur.includes(type);
        set({
          reactions: {
            ...state.reactions,
            [targetId]: has ? cur.filter((t) => t !== type) : [...cur, type],
          },
        });
        await apiClient.toggleReaction({ target_type: inferTargetType(state, targetId), target_id: targetId, reaction_type: type });
      },
      hasReaction: (targetId, type) => (get().reactions[targetId] ?? []).includes(type),

      stances: {},
      setStance: async (strainId, versionId, before, after, note, movedByNode) => {
        await apiClient.setStance({
          strain_id: strainId,
          version_id: versionId,
          before_score: before,
          after_score: after,
          note,
          moved_by_node_id: movedByNode ?? null,
        });
        set((s) => ({
          stances: {
            ...s.stances,
            [strainId]: {
              id: `local-${strainId}`,
              user_id: s.user?.id ?? "unknown",
              strain_id: strainId,
              version_id: versionId,
              before_score: before,
              after_score: after,
              note,
              moved_by_node_id: movedByNode ?? null,
              created_at: new Date().toISOString(),
            },
          },
        }));
      },
      getStance: (strainId) => get().stances[strainId],

      selectedNodeId: null,
      selectedEdgeId: null,
      positionOverrides: {},
      selectNode: (id) => set({ selectedNodeId: id, selectedEdgeId: null }),
      selectEdge: (id) => set({ selectedEdgeId: id, selectedNodeId: id ? null : get().selectedNodeId }),

      explorerMode: "canvas",
      setExplorerMode: (m) => set({ explorerMode: m }),

      getGraph: (slug) => get().graphs.find((g) => g.strain.slug === slug),

      addVariant: async (slug, nodeId, body) => {
        const graph = get().getGraph(slug);
        if (!graph) return;
        const created = await apiClient.addVariant(nodeId, graph.version.id, body);
        set((s) => ({
          graphs: s.graphs.map((g) => g.strain.slug === slug
            ? {
                ...g,
                nodes: g.nodes.map((n) => n.id === nodeId
                  ? { ...n, variants: [...(n.variants ?? []), created] }
                  : n),
              }
            : g),
        }));
      },

      preferVariant: async (slug, nodeId, variantId) => {
        // optimistic toggle; user's own prefer state lives in `reactions`
        const had = get().hasReaction(variantId, "prefer_wording");
        const delta = had ? -1 : 1;
        set((s) => ({
          reactions: {
            ...s.reactions,
            [variantId]: had
              ? (s.reactions[variantId] ?? []).filter((t) => t !== ("prefer_wording"))
              : [...(s.reactions[variantId] ?? []), "prefer_wording"],
          },
          graphs: s.graphs.map((g) => g.strain.slug === slug
            ? {
                ...g,
                nodes: g.nodes.map((n) => n.id === nodeId
                  ? {
                      ...n,
                      variants: (n.variants ?? []).map((v) => v.id === variantId
                        ? { ...v, prefers: Math.max(0, v.prefers + delta) }
                        : v),
                    }
                  : n),
              }
            : g),
        }));
        await apiClient.toggleReaction({ target_type: "node_variant", target_id: variantId, reaction_type: "prefer_wording" });
      },

      addModifier: async (slug, targetNodeId, kind, text) => {
        const graph = get().getGraph(slug);
        if (!graph) return;
        const created = await apiClient.addNode(graph.version.id, {
          type: "scope_condition",
          epistemic_kind: "empirical",
          text,
          provenance: "user",
          status: "active",
          position: { x: 0, y: 0 },
          badges: [],
          attachment_kind: kind,
        } as unknown as Omit<GraphNode, "id">);
        const edge = await apiClient.addEdge(graph.version.id, {
          source: created.id,
          target: targetNodeId,
          relation: "qualifies",
          label: kind,
          provenance: "user",
        } as Omit<GraphEdge, "id">);
        set((s) => ({
          graphs: s.graphs.map((g) => g.strain.slug === slug
            ? { ...g, nodes: [...g.nodes, created], edges: [...g.edges, edge] }
            : g),
        }));
      },

      // a modifier earns full nodehood by clearing its attachment kind
      promoteModifier: async (slug, nodeId) => {
        const graph = get().getGraph(slug);
        if (!graph) return;
        await apiClient.updateNode(nodeId, graph.version.id, { attachment_kind: null } as Partial<GraphNode>);
        set((s) => ({
          graphs: s.graphs.map((g) => g.strain.slug === slug
            ? { ...g, nodes: g.nodes.map((n) => n.id === nodeId ? { ...n, attachment: undefined } : n) }
            : g),
        }));
      },

      addNode: async (slug, node) => {
        const graph = get().getGraph(slug);
        if (!graph) return undefined;
        const created = await apiClient.addNode(graph.version.id, node);
        set((s) => ({
          graphs: s.graphs.map((g) => g.strain.slug === slug ? { ...g, nodes: [...g.nodes, created] } : g),
        }));
        return created;
      },

      updateNode: async (slug, nodeId, patch) => {
        const graph = get().getGraph(slug);
        if (!graph) return;
        const updated = await apiClient.updateNode(nodeId, graph.version.id, patch);
        set((s) => ({ graphs: s.graphs.map((g) => g.strain.slug === slug ? replaceNode(g, updated) : g) }));
      },

      removeNode: async (slug, nodeId) => {
        const graph = get().getGraph(slug);
        if (!graph) return;
        await apiClient.deleteNode(nodeId, graph.version.id);
        await get().loadGraph(slug, true);
      },

      addEdge: async (slug, edge) => {
        const graph = get().getGraph(slug);
        if (!graph) return undefined;
        const created = await apiClient.addEdge(graph.version.id, edge);
        set((s) => ({
          graphs: s.graphs.map((g) => g.strain.slug === slug ? { ...g, edges: [...g.edges, created] } : g),
        }));
        return created;
      },

      removeEdge: async (slug, edgeId) => {
        const graph = get().getGraph(slug);
        if (!graph) return;
        await apiClient.deleteEdge(edgeId, graph.version.id);
        await get().loadGraph(slug, true);
      },

      updateNodePosition: async (slug, nodeId, x, y) => {
        set((s) => ({
          graphs: s.graphs.map((g) =>
            g.strain.slug === slug
              ? { ...g, nodes: g.nodes.map((n) => (n.id === nodeId ? { ...n, position: { x, y } } : n)) }
              : g,
          ),
          positionOverrides: {
            ...s.positionOverrides,
            [slug]: { ...(s.positionOverrides[slug] ?? {}), [nodeId]: { x, y } },
          },
        }));
        const graph = get().getGraph(slug);
        if (graph) await apiClient.updateNode(nodeId, graph.version.id, { position: { x, y } });
      },

      getPositionOverride: (slug, nodeId) => get().positionOverrides[slug]?.[nodeId],
      clearPositionOverrides: (slug) =>
        set((s) => {
          const next = { ...s.positionOverrides };
          delete next[slug];
          return { positionOverrides: next };
        }),

      acceptInferredNode: async (slug, nodeId) =>
        get().updateNode(slug, nodeId, { provenance: "ai_confirmed", status: "active" }),
      rejectInferredNode: async (slug, nodeId) => get().removeNode(slug, nodeId),

      drafts: {},
      saveDraft: (key, draft) => set((s) => ({ drafts: { ...s.drafts, [key]: draft } })),

      publishDraft: async (key, slug, title, summary, tags, visibility = "public") => {
        const draft = get().drafts[key];
        if (!draft) return "";
        const graph = await apiClient.createStrain({
          slug,
          title,
          summary,
          original_text: draft.originalText,
          tags,
          visibility,
          nodes: draft.nodes,
          edges: draft.edges,
          variants: draft.thesis_variants,
        });
        set((s) => ({
          graphs: upsertGraph(s.graphs, graph),
          strains: upsertStrain(s.strains, graph.strain),
        }));
        return graph.strain.slug;
      },

      forkStrain: async (slug, intent, scope, changeSummary) => {
        const graph = get().getGraph(slug) ?? await get().loadGraph(slug);
        if (!graph) return "";
        const fork = await apiClient.forkVersion(graph.version.id, {
          intent,
          scope,
          change_summary: changeSummary,
        });
        set((s) => ({
          graphs: upsertGraph(s.graphs, fork),
          strains: upsertStrain(s.strains, fork.strain),
        }));
        return fork.strain.slug;
      },

      addComment: async (c) => {
        const created = await apiClient.addComment(c);
        set((s) => ({ comments: [...s.comments, created] }));
      },

      reset: () => set({
        graphs: [],
        strains: [],
        comments: [],
        reactions: {},
        stances: {},
        drafts: {},
        positionOverrides: {},
        selectedNodeId: null,
        selectedEdgeId: null,
        loggedIn: false,
        user: null,
      }),
    }),
    {
      name: "is.store.v2",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        theme: s.theme,
        reactions: s.reactions,
        stances: s.stances,
        loggedIn: s.loggedIn,
        user: s.user,
      }),
    },
  ),
);

// an expired session cookie must clear the persisted "logged in" UI state
// instead of leaving the app claiming a session it no longer has
setUnauthorizedHandler(() => {
  if (useStore.getState().loggedIn) {
    useStore.setState({ user: null, loggedIn: false });
  }
});
